/**
 * Sanctions choropleth — WM's sign for sanctions is a low-alpha country
 * wash in three ordered warm steps of decreasing opacity, a background
 * tint that never competes with point markers (DeckGLMap.ts:4717-4734).
 * Replaces the pile of identical amber centroid dots the 'sanctions'
 * toggle used to render; entity-level markers stay on 'sanctions-markers'.
 */

import { GeoJsonLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { Feature } from 'geojson';
import { getAllCountryFeatures } from '@/map/country-detect';

/** Layer IDs managed by this renderer. */
export const SANCTIONS_CHOROPLETH_LAYER_IDS = new Set(['sanctions']);

/** Country sanctions tiers — WM src/config/geo-map.ts:2638-2648, verbatim. */
export const SANCTIONED_COUNTRIES_ALPHA2: Record<string, 'severe' | 'high' | 'moderate'> = {
  KP: 'severe', // North Korea
  SS: 'severe', // South Sudan
  SY: 'severe', // Syria
  IR: 'high',   // Iran
  RU: 'high',   // Russia
  BY: 'high',   // Belarus
  VE: 'moderate', // Venezuela
  MM: 'moderate', // Myanmar
  CD: 'moderate', // Congo DRC
};

const TRANSPARENT: [number, number, number, number] = [0, 0, 0, 0];

/** Fill for a country code — WM DeckGLMap.ts:4727-4731. */
export function getSanctionsFill(code: string | undefined): [number, number, number, number] {
  const level = code ? SANCTIONED_COUNTRIES_ALPHA2[code.toUpperCase()] : undefined;
  if (level === 'severe') return [255, 0, 0, 89];
  if (level === 'high') return [255, 100, 0, 64];
  if (level === 'moderate') return [255, 200, 0, 51];
  return TRANSPARENT;
}

/** Create the sanctions choropleth layer. */
export function createSanctionsChoroplethLayer(): Layer[] {
  const features = getAllCountryFeatures();
  if (features.length === 0) return [];

  return [
    new GeoJsonLayer<Feature>({
      id: 'sanctions-choropleth-layer',
      data: { type: 'FeatureCollection' as const, features: features as unknown as Feature[] },
      filled: true,
      stroked: false,
      // Never steals clicks from the markers rendered on top (WM :4732)
      pickable: false,
      getFillColor: (f: Feature) => getSanctionsFill(f.properties?.code as string | undefined),
    }),
  ];
}
